<script  lang="ts">
    import { type User, EditUserDTO, ApiResultOfPublicUserDTO, AuthenticatedRequestOfEditUserDTO, Money, UserRole } from "@frontend/walletapi";
    import { service } from "@frontend/services";
    import { formatMoney } from "@frontend/Pages/Formats";
    import { translate, currentLanguage } from "../Translator";
    import { groups } from "@frontend/grouplist";
    import { GetCachedToken, me } from "@frontend/user";
    import { get } from "svelte/store";
   
    export let player: User;
    export let dismiss: () => void;
  
    let result: ApiResultOfPublicUserDTO | undefined = undefined;
    let editUser: EditUserDTO = {
      Id: player.Id,
      Email: player.Email,
      PhoneNumber: player.PhoneNumber,
      FirstName: player.FirstName,
      LastName: player.LastName,
      Enabled: player.Enabled,
      CountryCode: player.CountryCode,
      CurrencyCode: player.CurrencyCode,
      Reference: player.Reference,
      NewPassword: undefined,
      Txlimit: player.Txlimit
    }

    // Variables adicionales para campos que no están en EditUserDTO
    let selectedGroupId: number | undefined = player.GroupId;
    
    // Variables para manejar los inputs de límites como Money
    let txlimitFormatted: string = formatMoney(player.Txlimit);
    
    // Función para manejar el input de Txlimit
    function handleTxlimitInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const raw = input.value;
        
        // Remover caracteres no numéricos excepto punto decimal
        const numericValue = parseFloat(raw.replace(/[^0-9.]/g, ''));
        
        if (!isNaN(numericValue)) {
            const cents = Math.round(numericValue * 100);
            editUser.Txlimit = { InCents: cents };
            txlimitFormatted = formatMoney(cents);
            // Actualizar el valor del input sin el símbolo de dólar
            input.value = txlimitFormatted.replace('$', '');
        } else {
            editUser.Txlimit = undefined;
            txlimitFormatted = '';
            input.value = '';
        }
    }



    // Función para filtrar grupos disponibles según el rol del usuario
    function filterGroups(group: any) {
        // Si soy Owner, puedo ver todos los grupos
        if ($me?.Role === UserRole.Owner) {
            return true;
        }

        // Si no hay grupo de usuario definido
        if (!$me?.GroupId) {
            return false;
        }

        const myGroupId = $me.GroupId;

        // 1. Es mi grupo
        if (group.Id === myGroupId) {
            return true;
        }

        // 2. Es hijo directo de mi grupo
        if (group.ParentId === myGroupId) {
            return true;
        }

        // 3. Es descendiente (nieto o más profundo)
        let currentGroup = group;
        while (currentGroup?.ParentId) {
            if (currentGroup.ParentId === myGroupId) {
                return true;
            }
            // Obtener el valor actual del store
            const currentGroups = get(groups);
            currentGroup = currentGroups.find(g => g.Id === currentGroup?.ParentId);
        }

        return false;
    }
  
    let showPasswordInput = false;
    let newPasswordValue: string | undefined = undefined;
    let isSubmitting = false;
  
    async function EditCashier() {
        if (isSubmitting) return;
        
        // Validaciones básicas
        if (!editUser.FirstName?.trim()) {
            alert($translate('First Name is required'));
            return;
        }
        if (!editUser.LastName?.trim()) {
            alert($translate('Last Name is required'));
            return;
        }
        if (!editUser.Email?.trim()) {
            alert($translate('Email is required'));
            return;
        }

        isSubmitting = true;
        
        // Actualizar el NewPassword solo si estamos cambiando la contraseña
        if (showPasswordInput && newPasswordValue) {
            editUser.NewPassword = newPasswordValue;
        } else {
            editUser.NewPassword = undefined;
        }
  
        const requestPayload: AuthenticatedRequestOfEditUserDTO = {
            Token: GetCachedToken(), 
            Data: editUser, 
        };
  
        try {
            result = await service.walletapi.editUser(requestPayload); 
            console.log('Cashier editado con éxito:', result);
            
            if (result.Success) {
                // Auto-dismiss después de 2 segundos
                setTimeout(() => {
                    dismiss();
                }, 2000);
            }
        } catch (error) {
            console.error('Error al editar el cashier:', error);
            result = {
                Success: false,
                ErrorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        } finally {
            isSubmitting = false;
        }
    }
  </script>
  
     <div class="edit-cashier-container">
     <h2 class="ui centered dividing header">{$translate('Edit Cashier')} #{editUser.Id}</h2>
    
    {#if result && result.Success && result.Data}
             <div class="ui positive message">
         <div class="header">{$translate('Edited successfully')}</div>
       </div>
  
      <div class="ui segment">
                 <table class="receit-table">
           <tr><td class="receit-title">{$translate('Id')}</td><td class="receit-value">#{result.Data.Id}</td></tr>
           <tr><td class="receit-title">{$translate('Name')}</td><td class="receit-value">{result.Data.FirstName} {result.Data.LastName}</td></tr>
           <tr><td class="receit-title">{$translate('E-mail')}</td><td class="receit-value">{result.Data.Email ?? ""}</td></tr>
           <tr><td class="receit-title">{$translate('Phone Number')}</td><td class="receit-value">{result.Data.PhoneNumber ?? ""}</td></tr>
           <tr><td class="receit-title">{$translate('Group')}</td><td class="receit-value">{result.Data.GroupId ? $groups.find(g => g.Id === result.Data.GroupId)?.Name : 'No group'}</td></tr>


           <tr><td class="receit-title">{$translate('Transaction Limit')}</td><td class="receit-value money-value">{formatMoney(result.Data.Txlimit)}</td></tr>

         </table>
      </div>
  
             <div class="button-group">
         <button class="ui primary button" on:click={dismiss}>{$translate('Dismiss')}</button>
         <button class="ui button">{$translate('Print')}</button>
       </div>
    {:else}
             {#if result && !result.Success}
         <div class="ui negative message">
           <div class="header">{$translate('Error')}</div>
           <p>{result.ErrorMessage ?? "Unknown error when editing cashier"}</p>
         </div>
       {/if}
  
      <form class="ui form" on:submit|preventDefault={EditCashier}>
                 <div class="two fields">
           <div class="field">
             <label>{$translate('First Name')} *</label>
             <input type="text" placeholder={$translate('First Name')} bind:value={editUser.FirstName} required/>
           </div>
           <div class="field">
             <label>{$translate('Last Name')} *</label>
             <input type="text" placeholder={$translate('Last Name')} bind:value={editUser.LastName} required/>
           </div>
         </div>
  
                 <div class="two fields">
           <div class="field">
             <label>{$translate('E-mail')} *</label>
             <input type="email" placeholder={$translate('E-mail')} bind:value={editUser.Email} required/>
           </div>
           <div class="field">
             <label>{$translate('Phone Number')}</label>
             <input type="tel" placeholder={$translate('Phone Number')} bind:value={editUser.PhoneNumber}/>
           </div>
         </div>



                 <div class="field">
           <label>{$translate('Group')}</label>
           <select bind:value={selectedGroupId} class="ui dropdown">
             <option value={0}>{$translate('Select a group')}</option>
             {#each $groups.filter(filterGroups) as group}
               <option value={group.Id}>{group.Name}</option>
             {/each}
           </select>
         </div>

                 <div class="field">
           <label>{$translate('Transaction Limit')}</label>
           <div class="ui labeled input">
               <div class="ui label">$</div>
               <input 
                   type="text" 
                   placeholder="0.00" 
                   value={txlimitFormatted.replace('$', '')}
                   on:input={handleTxlimitInput}
                   class="money-input"
               />
           </div>
         </div>


  
                 <div class="field">
           <label>{$translate('Password')}</label>
           <div class="password-section">
             <div class="password-buttons">
               <button class="ui {!showPasswordInput ? 'positive' : 'basic'} button password-btn" 
                       on:click|preventDefault={() => {
                         showPasswordInput = false;
                         newPasswordValue = undefined;
                         editUser.NewPassword = undefined;
                       }}>
                 <i class="lock icon"></i>
                 {$translate('Keep current password')}
               </button>
               <button class="ui {showPasswordInput ? 'positive' : 'basic'} button password-btn" 
                       on:click|preventDefault={() => {
                         showPasswordInput = true;
                       }}>
                 <i class="key icon"></i>
                 {$translate('Set new password')}
               </button>
             </div>
             {#if showPasswordInput}
               <div class="password-input-container">
                 <input 
                   type="password" 
                   placeholder="{$translate('Enter new password')}" 
                   bind:value={newPasswordValue}
                   required={showPasswordInput}
                   class="password-input"
                 />
               </div>
             {/if}
           </div>
         </div> 
  
                 <div class="button-group">
           <button class="ui negative button" on:click|preventDefault={dismiss} disabled={isSubmitting}>{$translate('Cancel')}</button>
           <button class="ui positive button" type="submit" disabled={isSubmitting}>
             {#if isSubmitting}
               <i class="spinner loading icon"></i>
               {$translate('Saving')}...
             {:else}
               {$translate('Save')}
             {/if}
           </button>
         </div>
      </form>
    {/if}
  </div>
  
  <style>
    .edit-cashier-container {
      max-width: 700px;
      margin: 0 auto;
      padding: 1rem;
    }
  
    .button-group {
      display: flex;
      gap: 1rem;
      justify-content: flex-end;
      margin-top: 1rem;
    }
  
    .password-section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .password-buttons {
      display: flex;
      gap: 0.5rem;
      justify-content: space-between;
    }

    .password-btn {
      flex: 1;
      min-height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    .password-input-container {
      margin-top: 0.5rem;
    }

    .password-input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 1rem;
      transition: border-color 0.2s ease;
    }

    .password-input:focus {
      outline: none;
      border-color: #4caf50;
      box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
    }
  
    .receit-table {
      width: 100%;
      border-spacing: 0;
      font-family: 'Courier New', Courier, monospace;
    }
  
    .receit-table td {
      padding: 0.5rem 0;
      border-bottom: 1px solid #eee;
    }
  
    .receit-title {
      text-align: left;
      padding-right: 1rem;
      color: #666;
    }
  
    .receit-value {
      text-align: right;
      font-weight: 500;
    }
    
    .money-value {
      font-family: 'Courier New', Courier, monospace;
      color: #2c5aa0;
      font-weight: 600;
    }
  
    :global(.ui.form .field > label) {
      font-weight: 500;
      margin-bottom: 0.5rem;
    }

    :global(.ui.form .field > label:after) {
      content: '';
    }
  
    :global(.ui.form input) {
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 0.5rem;
    }

    :global(.ui.form select) {
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 0.5rem;
      background-color: white;
    }
    
    .money-input {
      text-align: right;
      font-family: 'Courier New', Courier, monospace;
      font-weight: 500;
    }
    
    .money-input::placeholder {
      color: #999;
      font-style: italic;
    }
    
    :global(.ui.labeled.input .ui.label) {
      background-color: #f8f9fa;
      border: 1px solid #ddd;
      border-right: none;
      color: #666;
      font-weight: 500;
    }

    :global(.ui.dropdown) {
      min-height: 2.5em;
    }
  </style>
  